// src/components/dashboard/StaleEstimates.js
import React from 'react';
import styled from 'styled-components';
import { 
  FaPaperPlane, 
  FaUser, 
  FaCalendarAlt, 
  FaEnvelope, 
  FaCheck,
  FaTimes,
  FaEye,
  FaExternalLinkAlt,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import DashboardCard from './DashboardCard';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../../utils/utils';

const EstimatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(2)};
`;

const EstimateItem = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(2)};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const EstimateHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing(1)};
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${props => props.theme.spacing(1)};
  }
`;

const EstimateId = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-size: ${props => props.theme.typography.fontSize.medium};
  color: ${props => props.theme.colors.textPrimary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
`;

const EstimateDate = styled.div`
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
`;

const EstimateContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing(1)};
`;

const CustomerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const RecipientInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const EstimateFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${props => props.theme.spacing(2)};
  padding-top: ${props => props.theme.spacing(1)};
  border-top: 1px solid ${props => props.theme.colors.inputGray}33;
`;

const EstimatePrice = styled.div`
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.success};
  font-size: ${props => props.theme.typography.fontSize.medium};
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.theme.spacing(0.5)} ${props => props.theme.spacing(1)};
  border-radius: 12px;
  background-color: ${props => getStatusColor(props.status)}22;
  color: ${props => getStatusColor(props.status)};
  font-size: ${props => props.theme.typography.fontSize.small};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  gap: ${props => props.theme.spacing(0.5)};
`;

const SentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(1)};
  font-size: ${props => props.theme.typography.fontSize.small};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: ${props => props.theme.spacing(1)};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing(0.5)};
  background-color: ${props => props.theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${props => props.theme.borderRadius};
  padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
  cursor: pointer;
  font-size: ${props => props.theme.typography.fontSize.small};
  
  &:hover {
    opacity: 0.9;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing(4)};
  color: ${props => props.theme.colors.textSecondary};
`;

// Helper to get color based on status
const getStatusColor = (status) => {
  switch (status) {
    case 'Sent':
      return '#2196f3'; // Blue
    case 'Viewed':
      return '#f9a825'; // Amber
    case 'Accepted':
      return '#4caf50'; // Green
    case 'Declined':
      return '#f44336'; // Red
    case 'Expired':
      return '#795548'; // Brown
    default:
      return '#9e9e9e'; // Gray
  }
};

// Helper to get status icon
const getStatusIcon = (status) => {
  switch (status) {
    case 'Viewed':
      return <FaEye />;
    case 'Accepted':
      return <FaCheck />;
    case 'Declined':
      return <FaTimes />;
    default:
      return null;
  }
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

// Helper to format time
const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const RecentSentEstimates = ({ data, loading, error }) => {
  const navigate = useNavigate();

  const handleEstimateClick = (estimateId) => {
    navigate(`/estimates/view/${estimateId}`);
  };

  return (
    <DashboardCard
      title="Stale Estimates"
      icon={<FaPaperPlane />}
      viewAllLink="/estimates"
      loading={loading}
      error={error}
    >
      {data && data.length > 0 ? (
        <EstimatesWrapper>
          {data.map((estimate) => (
            <EstimateItem 
              key={estimate.id} 
              onClick={() => handleEstimateClick(estimate.id)}
            >
              <EstimateHeader>
                <EstimateId>
                  <FaFileInvoiceDollar />
                  Estimate #{estimate.id}
                </EstimateId>
                <StatusBadge status={estimate.status}>
                  {getStatusIcon(estimate.status)}
                  {estimate.status}
                </StatusBadge>
              </EstimateHeader>
              
              <EstimateContent>
                <CustomerInfo>
                  <FaUser />
                  {estimate.customer_name || estimate.Lead?.name || 'No Customer'}
                </CustomerInfo>
                
                <RecipientInfo>
                  <FaEnvelope />
                  {estimate.recipient_email || 'No Email'}
                </RecipientInfo>
                
                <SentInfo>
                  <FaPaperPlane />
                  Sent on {formatDate(estimate.sent_date)} at {formatTime(estimate.sent_date)}
                </SentInfo>
                
                {estimate.viewed_date && (
                  <SentInfo>
                    <FaEye />
                    Viewed on {formatDate(estimate.viewed_date)} at {formatTime(estimate.viewed_date)}
                  </SentInfo>
                )}
                
                <EstimateFooter>
                  <EstimatePrice>
                    {formatCurrency(estimate.totalPrice)}
                  </EstimatePrice>
                  
                  <ViewButton onClick={(e) => {
                    e.stopPropagation();
                    handleEstimateClick(estimate.id);
                  }}>
                    View Details
                    <FaExternalLinkAlt />
                  </ViewButton>
                </EstimateFooter>
              </EstimateContent>
            </EstimateItem>
          ))}
        </EstimatesWrapper>
      ) : (
        <EmptyState>No recently stale estimates found</EmptyState>
      )}
    </DashboardCard>
  );
};

export default RecentSentEstimates;